// Sector-specific context questions — organisation-level characteristics that a
// tenant self-reports once, so the benchmark can group organisations on them
// ("orgs that do energiecoaching grow faster on effect X").
//
// This is the SELF-REPORTED counterpart to the derived CBS context family in
// functions/externalContext/. Every answer is therefore a closed vocabulary:
// buildDimensionRows buckets on the exact stored string and has no per-row
// minimum-organisation floor, so one org's free-text phrasing would produce a
// phantom "top performers do X" row.
//
// ⚠️ These questions are DECCOS-OWNED. They do not come from the meetstandaard
// API (verified against the live energiearmoede 0.9 document — it has stellingen
// and situatieschetsen, nothing resembling "worden er wervingsacties
// uitgevoerd?"). That is why this lives in dataHelpers/ next to the other
// taxonomies and not in helpers/meetstandaard/.
//
// SYNC: this file WILL be mirrored as functions/data/sectorContextQuestions.js
// (src/ and functions/ are separate npm packages — no shared module boundary),
// pinned by a vm parity test. Neither exists yet — that is T5 of the feature
// spec, needed before the benchmark can read these answers. Once it lands, edit
// both in the same commit.
// See docs/patterns.md#data-mirrored-constants and #data-taxonomy.
//
// AI-CONTEXT: See docs/features/sector-context-questions.md
// AI-CONTEXT: See docs/decisions.md (ADR-031 + its 2026-08-17 amendment)

/**
 * @typedef {object} SectorContextOption
 * @property {string} value - Stored answer; also the benchmark dimension value
 * @property {string} label - Dutch UI label
 */

/**
 * @typedef {object} SectorContextDependency
 * @property {string} question - Id of the question this one hangs off
 * @property {string} value - The parent answer that reveals this question
 */

/**
 * @typedef {object} SectorContextQuestion
 * @property {string} id - snake_case, globally unique, written to Firestore
 * @property {string} label - The question as the user reads it
 * @property {string} benchmarkPrefix - Dutch prefix for the Inzichten sentence
 * @property {SectorContextOption[]} options - Closed vocabulary, never free text
 * @property {SectorContextDependency} [dependsOn] - Only shown when satisfied
 */

/**
 * @typedef {object} SectorContextGroup
 * @property {string} id - Group key
 * @property {string} label - Group heading
 * @property {SectorContextQuestion[]} questions
 */

// ─── Shared option sets ───────────────────────────────────────────────────────

// Strings, never booleans. These are dimension VALUES that get compared across
// tenants, and a boolean round-tripped through `!!`/`Boolean()` is exactly the
// posNeg incident. See docs/pitfalls.md#database-string-enum-boolean-coercion.
const JA_NEE = [
  { value: 'ja', label: 'Ja' },
  { value: 'nee', label: 'Nee' },
]

/** A yes/no question — the shape most of these take. */
const jaNee = (id, label, benchmarkPrefix) => ({ id, label, benchmarkPrefix, options: JA_NEE })

// ─── The questions ────────────────────────────────────────────────────────────

/** @type {Record<string, {groups: SectorContextGroup[]}>} */
export const sectorContextQuestions = {

  energiearmoede: {
    groups: [
      {
        id: 'activiteiten',
        label: 'Welke activiteiten worden gedaan?',
        questions: [
          jaNee('energiebesparende_maatregelen', 'Worden er energiebesparende maatregelen uitgevoerd?', 'energiebesparende maatregelen'),
          {
            id: 'maatregelen_levering',
            label: 'Worden deze aangebracht of aangeleverd?',
            benchmarkPrefix: 'levering van maatregelen',
            options: [
              { value: 'aangebracht', label: 'Aangebracht' },
              { value: 'aangeleverd', label: 'Aangeleverd' },
              { value: 'beide', label: 'Beide' },
            ],
            dependsOn: { question: 'energiebesparende_maatregelen', value: 'ja' },
          },
          jaNee('energiecoaching', 'Wordt er energiecoaching uitgevoerd?', 'energiecoaching'),
          jaNee('wervingsacties', 'Worden er wervingsacties uitgevoerd?', 'wervingsacties'),
          jaNee('grootschalige_isolatie', 'Worden er grote maatregelen uitgevoerd (zoals dakisolatie of muurisolatie)?', 'grote maatregelen'),
          jaNee('witgoedregelingen', 'Worden er witgoed-regelingen uitgevoerd?', 'witgoed-regelingen'),
          jaNee('trainingen_uitvoerders', 'Worden er trainingen gedaan voor de coaches en/of fixers, zoals cultureel sensitieve trainingen?', 'trainingen voor uitvoerders'),
          jaNee('intern_leerwerktraject', 'Is er een intern leer-werktraject?', 'intern leer-werktraject'),
        ],
      },
      {
        id: 'kenmerken',
        label: 'Andere kenmerken',
        questions: [
          jaNee('winstoogmerk', 'Werkt de organisatie vanuit een winstoogmerk?', 'winstoogmerk'),
          {
            id: 'jaren_actief_in_wijk',
            label: 'Hoe lang is de organisatie actief in de wijk?',
            benchmarkPrefix: 'jaren actief in de wijk',
            // Bands, not a number: free numeric input can't be bucketed across
            // tenants. Finer at the short end, where the difference says most
            // about a young initiative.
            options: [
              { value: 'lt_1', label: 'Minder dan 1 jaar' },
              { value: '1_3', label: '1-3 jaar' },
              { value: '3_5', label: '3-5 jaar' },
              { value: '5_10', label: '5-10 jaar' },
              { value: 'gt_10', label: 'Meer dan 10 jaar' },
            ],
          },
          jaNee('lokaal_initiatief', 'Is de organisatie gestart uit een lokaal initiatief?', 'lokaal initiatief'),
          // The source document asked these as ONE question ("vrijwilligers of
          // mensen met afstand tot de arbeidsmarkt"). Split on the domain
          // owner's sign-off: as a single yes/no the answer is unreadable, and
          // the two are different things — extra capacity vs. the target group
          // doing the work.
          jaNee('inzet_vrijwilligers', 'Wordt er gebruikgemaakt van vrijwilligers?', 'inzet van vrijwilligers'),
          jaNee('inzet_afstand_arbeidsmarkt', 'Wordt er gebruikgemaakt van mensen met een afstand tot de arbeidsmarkt?', 'inzet van mensen met afstand tot de arbeidsmarkt'),
        ],
      },
    ],
  },

  arbeidsparticipatie: {
    groups: [
      {
        id: 'kenmerken',
        label: 'Kenmerken van de organisatie',
        questions: [
          {
            // Besluit 2026-10-13: de omvang van een organisatie, zodat de
            // intelligence kan toetsen of organisaties met meer capaciteit
            // anders presteren. Banden, geen getal — een vrij getal is niet te
            // groeperen over organisaties heen (zie jaren_actief_in_wijk).
            id: 'aantal_fte',
            label: 'Hoeveel fte aan betaalde medewerkers heeft de organisatie?',
            benchmarkPrefix: 'omvang in fte',
            options: [
              { value: 'lt_2', label: 'Minder dan 2 fte' },
              { value: '2_5', label: '2-5 fte' },
              { value: '5_10', label: '5-10 fte' },
              { value: '10_25', label: '10-25 fte' },
              { value: '25_50', label: '25-50 fte' },
              { value: 'gt_50', label: 'Meer dan 50 fte' },
            ],
          },
        ],
      },
    ],
  },

}

// ─── Selectors ────────────────────────────────────────────────────────────────

/**
 * The question blocks an organisation in the given sectors should be asked.
 *
 * ⚠️ FAILS CLOSED, deliberately. `mechanismCategoriesForSector([])` returns
 * EVERYTHING — an empty sector list there means "don't narrow the dropdown".
 * This one returns NOTHING. A whole block of sector-specific questions has no
 * meaning for a tenant in another sector, so showing it invites wrong data into
 * a shared benchmark vocabulary. Do not "fix" this to match the other taxonomy:
 * see the 2026-08-17 amendment to ADR-031, which draws exactly this line.
 *
 * @param {string[]} [sectors] - Sector slugs, typically CompagnyMeta.SelectedSectors.
 * @returns {Array<{sector: string, groups: SectorContextGroup[]}>} Empty when nothing applies.
 */
export const sectorContextGroupsFor = (sectors) => {
  const list = Array.isArray(sectors) ? sectors.filter(Boolean) : []
  return list
    .filter((slug) => sectorContextQuestions[slug])
    .map((slug) => ({ sector: slug, groups: sectorContextQuestions[slug].groups }))
}

/**
 * Every question across every sector, flattened, each tagged with its sector.
 * Feeds the benchmark dimension list and the id-uniqueness guard.
 *
 * @returns {Array<SectorContextQuestion & {sector: string, group: string}>}
 */
export const allSectorContextQuestions = () =>
  Object.entries(sectorContextQuestions).flatMap(([sector, { groups }]) =>
    groups.flatMap((group) =>
      group.questions.map((question) => ({ ...question, sector, group: group.id }))
    )
  )

/**
 * The benchmark dimension key for a question.
 *
 * Derived from the bare question id, NOT from sector + id, so the key stays
 * readable and stable.
 *
 * ⚠️ Sharing a question between two sectors is NOT possible in the current
 * shape. This config is keyed per sector slug (ADR-031 amendment, rule 1), so
 * asking the same question in two sectors means duplicating the id — which the
 * uniqueness test rejects and which would emit a duplicate benchmark dimension.
 * When a second sector needs a question this one already asks, move the config
 * to one tagged list (`sectors: string[]` per question) first; the dimension key
 * does not change, so stored answers survive.
 *
 * @param {string} questionId - e.g. 'jaren_actief_in_wijk'
 * @returns {string} e.g. 'sectorCtxJarenActiefInWijk'
 */
export const sectorContextDimName = (questionId) =>
  `sectorCtx${String(questionId)
    .split('_')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join('')}`

/**
 * Whether a question should be shown, given the answers already given.
 * A question without `dependsOn` is always shown.
 *
 * @param {SectorContextQuestion} question
 * @param {Record<string, string>} [answers] - This sector's stored answers.
 * @returns {boolean}
 */
export const dependsOnSatisfied = (question, answers) => {
  const rule = question && question.dependsOn
  if (!rule) return true
  return Boolean(answers) && answers[rule.question] === rule.value
}

/**
 * Dimension key → { answerValue: Dutch label }, for benchmarkConclusions' LABELS.
 * Derived from the config so there is no second hand-maintained list.
 *
 * @returns {Record<string, Record<string, string>>}
 */
export const sectorContextDimensionLabels = () =>
  Object.fromEntries(
    allSectorContextQuestions().map((q) => [
      sectorContextDimName(q.id),
      Object.fromEntries(q.options.map((o) => [o.value, o.label])),
    ])
  )

/**
 * Dimension key → Dutch prefix for the conclusion sentence, for
 * benchmarkConclusions' DIMENSION_PREFIX. Without it a finding reads as a
 * floating "Ja" with no indication of which question it answers.
 *
 * @returns {Record<string, string>}
 */
export const sectorContextDimensionPrefixes = () =>
  Object.fromEntries(
    allSectorContextQuestions().map((q) => [sectorContextDimName(q.id), q.benchmarkPrefix])
  )

// ─── Aggregate default export ─────────────────────────────────────────────────

const sectorContextTaxonomy = {
  sectorContextQuestions,
  sectorContextGroupsFor,
  allSectorContextQuestions,
  sectorContextDimName,
  dependsOnSatisfied,
  sectorContextDimensionLabels,
  sectorContextDimensionPrefixes,
}

export default sectorContextTaxonomy
